import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * Server-authoritative pricing for an area-priced line.
 *
 * The client's number IS the billed number — `requestServiceModel` stores
 * whatever quantity the request carries and the creation path never recomputes
 * — so two implementations that agree today can drift tomorrow with nothing to
 * catch it. This endpoint is the single authority; `marineCalc` stays for the
 * live figure the estimator watches while typing, and must not be what is saved.
 *
 * **Send the id, not the formula.** `installerServiceId` is the only required
 * field: the server reads price, `area_formula`, `area_factor`, `waste_pct`,
 * `duration_days` and `duration_unit` from the database. A stale client copy of
 * a formula therefore cannot price a job — which is exactly how service 490's
 * bad formula cost two rounds of investigation.
 *
 * The craft is identified by `vin` when it is saved. Supplied dimensions
 * OVERLAY the stored row rather than replacing it, so sending only a corrected
 * freeboard keeps the stored length.
 */

/** Our snake_case formula variables → the request's camelCase dimension keys. */
const DIMENSION_KEYS = {
  loa_ft: 'loaFt',
  beam_ft: 'beamFt',
  hull_height_in: 'hullHeightIn',
  boat_class: 'boatClass',
  // A formula VARIABLE, not an override: waste still applies to it, and it only
  // works for services whose formula references it. Not interchangeable with
  // overrideSqFt — see below.
  manual_area_sqft: 'manualAreaSqft',
};

/**
 * @param {object} input
 * @param {number|string} input.installerServiceId  required
 * @param {string} [input.vin]                      the saved craft
 * @param {object} [input.variables]                snake_case dimensions
 * @param {number} [input.overrideSqFt]  the estimator's FINAL billable area —
 *   the formula is bypassed and neither area_factor nor waste is re-applied,
 *   only CEIL. Correct, because the field the estimator edits is seeded with the
 *   formula's FINISHED figure: re-applying waste would silently inflate a
 *   hand-entered 250 to 263.
 * @param {number} [input.wastePct]  replaces the catalog waste for this quote
 * @returns {Promise<object>} quantity, unit_price, service_amount, pricing_kind,
 *   duration_days, plus `overridden`, `formula_quantity`, `waste_pct_source`,
 *   `catalog_waste_pct`, `formula_error` and the `calculation` block.
 */
export async function priceMarineLineOnServer({
  installerServiceId,
  vin,
  variables,
  overrideSqFt,
  wastePct,
} = {}) {
  if (installerServiceId === null || installerServiceId === undefined || installerServiceId === '') {
    throw new Error('installerServiceId is required to price a marine line.');
  }

  const body = { installerServiceId };
  if (vin) body.vin = vin;

  const vars = variables || {};
  Object.entries(DIMENSION_KEYS).forEach(([ours, theirs]) => {
    const v = vars[ours];
    if (v === null || v === undefined || v === '') return;
    const n = Number(v);
    if (Number.isFinite(n)) body[theirs] = n;
  });

  // Sent only when the estimator actually overrode. Sending the computed figure
  // back as an override would make every line report `overridden: true` and lose
  // the distinction the response exists to preserve.
  if (Number.isFinite(Number(overrideSqFt)) && Number(overrideSqFt) > 0) {
    body.overrideSqFt = Number(overrideSqFt);
  }
  if (Number.isFinite(Number(wastePct))) {
    body.wastePct = Number(wastePct);
  }

  const { data } = await API.post('/api/marine/price', body);
  return data;
}

const marinePriceService = { priceMarineLineOnServer };
export default marinePriceService;
